import {
    Fragment,
    useMemo,
    type FC,
    useId
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IQuestionnaireItemProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    QuestionnairePartContentProps,
    QuestionnaireItemContextType
} from "../../type";
import {
    QuestionnaireItemContext,
    withQuestionnaireContext
} from "../../context";
import QuestionnaireDescription from "../description";
import QuestionnaireSwap from "../../internal/swap";
import QuestionnaireChoices from "../choices";
import QuestionnaireError from "../error";
import QuestionnaireInput from "../input";
import QuestionnaireTitle from "../title";
import Card from "../../../card";

const QuestionnaireItemContent: FC<QuestionnairePartContentProps<IQuestionnaireItemProps>> = ({
    customTheme: customThemeProp,
    item: itemProp,
    children,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        body: bodyDynamicStyle
    } = useStyles({
        spaces
    });

    const baseId = useId().replace(/[^a-zA-Z0-9]/g, "");

    const item = itemProp ?? context.activeItem;

    const isInvalid = !!item && context.errorName === item.name;

    const isLocked = context.isLoading || context.isDisabled || context.isSubmitted;

    const itemContext = useMemo<QuestionnaireItemContextType | undefined>(() => item ? {
        descriptionId: `${baseId}-description`,
        answer: context.answers[item.name],
        errorId: `${baseId}-error`,
        titleId: `${baseId}-title`,
        isInvalid,
        isLocked,
        item
    } : undefined, [
        context.answers,
        isInvalid,
        isLocked,
        baseId,
        item
    ]);

    if(!item || !itemContext) {
        return null;
    }

    const describedBy = [
        item.description ? itemContext.descriptionId : "",
        isInvalid ? itemContext.errorId : ""
    ].filter((id) => id.length > 0).join(" ");

    const accessibilityProps = Platform.OS === "web" ? {
        "aria-describedby": describedBy.length ? describedBy : undefined,
        "aria-labelledby": itemContext.titleId
    } : {
        accessibilityLabel: item.title
    };

    const renderDefault = () => {
        return <Fragment>
            <QuestionnaireTitle />
            <View
                style={[
                    stylesheet.body,
                    bodyDynamicStyle
                ]}
            >
                {
                    item.description ?
                        <QuestionnaireDescription />
                    :
                        null
                }
                {
                    item.choices?.length ?
                        <QuestionnaireChoices />
                    :
                        null
                }
                {
                    item.input ?
                        <QuestionnaireInput />
                    :
                        null
                }
                {
                    isInvalid ?
                        <QuestionnaireError />
                    :
                        null
                }
            </View>
        </Fragment>;
    };

    return <QuestionnaireItemContext.Provider
        value={itemContext}
    >
        <Card.Content>
            <QuestionnaireSwap
                key={itemProp ? undefined : context.activeIndex}
                step={itemProp ? 0 : context.swapStep}
                customTheme={customTheme}
            >
                <View
                    {...props}
                    {...accessibilityProps}
                    aria-invalid={isInvalid ? true : undefined}
                    style={[
                        style,
                        stylesheet.container
                    ]}
                    ref={context.contentRef}
                    tabIndex={-1}
                    role="group"
                >
                    {children ?? renderDefault()}
                </View>
            </QuestionnaireSwap>
        </Card.Content>
    </QuestionnaireItemContext.Provider>;
};
export default withQuestionnaireContext("Item", QuestionnaireItemContent);
